import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useI18n } from '../i18n';
import { startGoogleLogin, useAuth } from '../auth/AuthContext';
import { Mail, Lock, User as UserIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Algo ha fallado. Inténtalo de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.5C16.9 3.5 14.7 2.5 12 2.5 6.9 2.5 2.8 6.6 2.8 12s4.1 9.5 9.2 9.5c5.3 0 8.8-3.7 8.8-9 0-.6-.1-1.1-.1-1.6H12z"/>
  </svg>
);

const inp = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg pl-10 pr-4 py-2.5 text-white text-sm';
const lbl = 'block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2';

const AuthLayout = ({ mode }) => {
  const { t } = useI18n();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const isLogin = mode === 'login';
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || (!isLogin && !form.name)) { toast.error('Completa todos los campos'); return; }
    if (!isLogin && form.password.length < 8) { toast.error('La contraseña debe tener al menos 8 caracteres'); return; }
    setBusy(true);
    try {
      const body = isLogin ? { email: form.email, password: form.password } : { email: form.email, password: form.password, name: form.name };
      const res = await fetch(`${API}/auth/${isLogin ? 'login' : 'register'}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(formatApiErrorDetail(data.detail)); return; }
      await checkAuth();
      toast.success(isLogin ? 'Sesión iniciada' : '¡Cuenta creada!');
      navigate('/');
    } catch { toast.error('No se pudo conectar. Inténtalo de nuevo.'); }
    finally { setBusy(false); }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Introduce tu email'); return; }
    setBusy(true);
    try {
      await fetch(`${API}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ email: form.email }) });
      toast.success('Si el email existe, te enviaremos instrucciones para restablecer la contraseña.');
      setForgot(false);
    } catch { toast.error('No se pudo procesar la solicitud.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-12 relative">
        <div className="absolute inset-0 sc-grid-pattern opacity-30 pointer-events-none" />
        <div className="relative sc-card p-8 md:p-10 w-full max-w-md" data-testid={`${mode}-form`}>
          <div className="sc-mono-eyebrow text-amber-500 mb-3">Stage Crew</div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {forgot ? 'Recuperar contraseña' : (isLogin ? t('auth.login.title') : t('auth.register.title'))}
          </h1>
          <p className="text-sm text-slate-400 mb-8">{forgot ? 'Introduce tu email y te enviaremos un enlace para crear una nueva contraseña.' : (isLogin ? t('auth.login.subtitle') : t('auth.register.subtitle'))}</p>

          {forgot ? (
            <form className="space-y-4" onSubmit={sendForgot}>
              <div>
                <label className={lbl}>{t('auth.email')}</label>
                <div className="relative"><Mail size={15} className="absolute left-3 top-3 text-slate-500" /><input data-testid="forgot-email-input" type="email" className={inp} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="tu@email.com" /></div>
              </div>
              <button type="submit" data-testid="forgot-submit-btn" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-4 py-3 rounded-lg transition-colors">{busy ? 'Enviando…' : 'Enviar enlace'}</button>
              <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-slate-400 hover:text-amber-500">← Volver</button>
            </form>
          ) : (
            <>
              <button onClick={startGoogleLogin} data-testid="google-signin-btn" className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-100 text-slate-900 font-semibold px-4 py-3 rounded-lg transition-colors mb-6">
                <GoogleIcon />
                <span>{isLogin ? 'Entrar con Google' : 'Continuar con Google'}</span>
              </button>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-[#2A2F3D]" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">o con email</span>
                <div className="flex-1 h-px bg-[#2A2F3D]" />
              </div>

              <form className="space-y-4" onSubmit={submit}>
                {!isLogin && (
                  <div>
                    <label className={lbl}>{t('auth.name')}</label>
                    <div className="relative"><UserIcon size={15} className="absolute left-3 top-3 text-slate-500" /><input data-testid="auth-name-input" className={inp} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Tu nombre" /></div>
                  </div>
                )}
                <div>
                  <label className={lbl}>{t('auth.email')}</label>
                  <div className="relative"><Mail size={15} className="absolute left-3 top-3 text-slate-500" /><input data-testid="auth-email-input" type="email" className={inp} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="tu@email.com" /></div>
                </div>
                <div>
                  <label className={lbl}>{t('auth.password')}</label>
                  <div className="relative"><Lock size={15} className="absolute left-3 top-3 text-slate-500" /><input data-testid="auth-password-input" type="password" className={inp} value={form.password} onChange={(e) => set('password', e.target.value)} placeholder={isLogin ? '••••••••' : 'Mínimo 8 caracteres'} /></div>
                </div>
                <button type="submit" data-testid="auth-submit-btn" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-4 py-3 rounded-lg transition-colors">
                  {busy ? (isLogin ? 'Entrando…' : 'Creando cuenta…') : (isLogin ? t('auth.submit.login') : t('auth.submit.register'))}
                </button>
                {isLogin && (
                  <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)} className="w-full text-xs text-slate-400 hover:text-amber-500 font-mono uppercase tracking-wider">No me acuerdo de la contraseña</button>
                )}
              </form>
            </>
          )}

          <div className="pt-6 mt-6 border-t border-[#2A2F3D] text-center text-sm">
            {isLogin ? (
              <span className="text-slate-400">{t('auth.noAccount')} <Link to="/registro" className="text-amber-500 hover:text-amber-400 font-medium">{t('auth.submit.register')}</Link></span>
            ) : (
              <span className="text-slate-400">{t('auth.hasAccount')} <Link to="/login" className="text-amber-500 hover:text-amber-400 font-medium">{t('auth.submit.login')}</Link></span>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const LoginPage = () => <AuthLayout mode="login" />;
export const RegisterPage = () => <AuthLayout mode="register" />;

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('La contraseña debe tener al menos 8 caracteres'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ token, password }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(formatApiErrorDetail(data.detail)); return; }
      toast.success('Contraseña actualizada. Ya puedes iniciar sesión.');
      navigate('/login');
    } catch { toast.error('No se pudo restablecer la contraseña.'); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0B0C0E] flex flex-col">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-12 relative">
        <div className="absolute inset-0 sc-grid-pattern opacity-30 pointer-events-none" />
        <div className="relative sc-card p-8 md:p-10 w-full max-w-md" data-testid="reset-password-form">
          <div className="sc-mono-eyebrow text-amber-500 mb-3">Stage Crew</div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Nueva contraseña</h1>
          {!token ? (
            <p className="text-sm text-red-300 mt-4" data-testid="reset-no-token">Enlace no válido. Solicita uno nuevo desde «¿Olvidaste tu contraseña?».</p>
          ) : (
            <form className="space-y-4 mt-6" onSubmit={submit}>
              <div>
                <label className={lbl}>Nueva contraseña</label>
                <div className="relative"><Lock size={15} className="absolute left-3 top-3 text-slate-500" /><input data-testid="reset-password-input" type="password" className={inp} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" /></div>
              </div>
              <button type="submit" data-testid="reset-submit-btn" disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-4 py-3 rounded-lg transition-colors">{busy ? 'Guardando…' : 'Guardar contraseña'}</button>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
